import React, { useState, useEffect } from "react";
import {
  useParams
} from "react-router-dom";
import {
  Loader2,
  AlertTriangle,
  GraduationCap
} from "lucide-react";
import {
  getStudentAttendance
} from "../../api/studentattendance/studentAttendancePortalApi";
import {
  Card,
  CardContent,
  Badge,
  Skeleton
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

const STATUS_BADGE_VARIANTS = {
  PRESENT: "success",
  ABSENT: "danger",
  LATE: "warning",
  HOLIDAY: "primary",
  LEAVE: "purple",
};

const dayName = (dateStr) => {
  try {
    return new Date(dateStr).toLocaleDateString("en-US", { weekday: "short" });
  } catch {
    return "";
  }
};

const StudentAttendanceView = () => {
  const { studentId } = useParams();
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  const load = async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const res = await getStudentAttendance(studentId, { year, month });
      setData(res);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to load attendance");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, year, month]);

  const student = data?.student || {};
  const summary = data?.summary || {};
  const records = data?.records || [];

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6">
      <div className="pb-4 border-b border-neutral-200 dark:border-neutral-800/80">
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
          Student Attendance
        </h1>
        <p className="text-xs sm:text-sm font-semibold text-neutral-500 dark:text-neutral-400 mt-1">
          Individual monthly attendance report and overview
        </p>
      </div>

      {/* Date Filters */}
      <Card className="border border-neutral-200 dark:border-neutral-800/80 bg-white dark:bg-neutral-900 rounded-2xl p-4 shadow-xs">
        <div className="flex flex-wrap items-center gap-3">
          <div>
            <label className="block text-[11px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1">
              Year
            </label>
            <input
              type="number"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="border border-neutral-200 dark:border-neutral-800 rounded-xl px-3 py-1.5 text-xs font-bold text-neutral-800 dark:text-neutral-100 bg-neutral-50/50 dark:bg-neutral-950 shadow-2xs w-28 outline-none"
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1">
              Month
            </label>
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="border border-neutral-200 dark:border-neutral-800 rounded-xl px-3 py-1.5 text-xs font-bold text-neutral-800 dark:text-neutral-100 bg-neutral-50/50 dark:bg-neutral-950 shadow-2xs outline-none"
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {new Date(2000, i).toLocaleString("default", {
                    month: "long",
                  })}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      

      {loading ? (
        <div className="space-y-4">
          <Skeleton height={90} width="100%" className="rounded-2xl" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Skeleton height={80} width="100%" className="rounded-xl" />
            <Skeleton height={80} width="100%" className="rounded-xl" />
            <Skeleton height={80} width="100%" className="rounded-xl" />
            <Skeleton height={80} width="100%" className="rounded-xl" />
          </div>
          <Skeleton height={200} width="100%" className="rounded-2xl" />
        </div>
      ) : (
        <>
          {/* Student Profile Card */}
          <Card className="border border-neutral-200 dark:border-neutral-800/80 bg-white dark:bg-neutral-900 rounded-2xl p-5 shadow-xs">
            <div className="flex items-center gap-3.5">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 ring-1 ring-indigo-500/20 shadow-2xs">
                <GraduationCap size={24} />
              </span>
              <div>
                <h2 className="text-base sm:text-lg font-bold text-neutral-900 dark:text-neutral-100 leading-tight">
                  {student.name || "—"}
                </h2>
                <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 mt-0.5">
                  Admission No: {student.admissionNo || "—"}
                  {student.rollNumber ? ` · Roll: ${student.rollNumber}` : ""}
                </p>
              </div>
            </div>
          </Card>

          {/* Summary metric grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="border border-emerald-200/80 bg-emerald-50/50 p-4 rounded-2xl text-center shadow-2xs">
              <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Present</p>
              <p className="text-2xl font-bold text-emerald-900 mt-1 tabular-nums">
                {summary.present ?? 0}
              </p>
            </Card>

            <Card className="border border-rose-200/80 bg-rose-50/50 p-4 rounded-2xl text-center shadow-2xs">
              <p className="text-[10px] font-bold uppercase tracking-wider text-rose-700">Absent</p>
              <p className="text-2xl font-bold text-rose-900 mt-1 tabular-nums">
                {summary.absent ?? 0}
              </p>
            </Card>

            <Card className="border border-amber-200/80 bg-amber-50/50 p-4 rounded-2xl text-center shadow-2xs">
              <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Late</p>
              <p className="text-2xl font-bold text-amber-900 mt-1 tabular-nums">
                {summary.late ?? 0}
              </p>
            </Card>

            <Card className="border border-sky-200/80 bg-sky-50/50 p-4 rounded-2xl text-center shadow-2xs">
              <p className="text-[10px] font-bold uppercase tracking-wider text-sky-700">Attendance Rate</p>
              <p className="text-2xl font-bold text-sky-900 mt-1 tabular-nums">
                {summary.attendancePercentage ?? 0}%
              </p>
            </Card>
          </div>

          {/* Records table */}
          <Card className="border border-neutral-200 dark:border-neutral-800/80 bg-white dark:bg-neutral-900 rounded-2xl overflow-hidden shadow-xs">
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead className="bg-slate-50/80 text-neutral-600 dark:text-neutral-300 font-extrabold border-b border-neutral-200 dark:border-neutral-800">
                  <tr>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Day</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Remark</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-neutral-800">
                  {records.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-neutral-500 dark:text-neutral-400 font-semibold">
                        No attendance records for this period
                      </td>
                    </tr>
                  ) : (
                    records.map((r, i) => (
                      <tr key={i} className="hover:bg-neutral-50 dark:hover:bg-neutral-800/60 transition-colors">
                        <td className="px-4 py-3 font-bold text-neutral-800 dark:text-neutral-100">{r.date}</td>
                        <td className="px-4 py-3 text-neutral-500 dark:text-neutral-400 font-semibold">
                          {dayName(r.date)}
                        </td>
                        <td className="px-4 py-3">
                          <Badge
                            variant={STATUS_BADGE_VARIANTS[r.status] || "secondary"}
                            className="px-2.5 py-0.5 text-[10px] font-extrabold uppercase rounded-full"
                          >
                            {r.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-neutral-500 dark:text-neutral-400 font-medium">
                          {r.remark || "—"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

export default StudentAttendanceView;